/* eslint-disable @typescript-eslint/no-empty-function */
import { forwardRef, ReactNode } from 'react';
import clsx from 'clsx';

// components
import Typography, { TypographyProps } from '@/components/elements/Typography';
import Spinner from '@/components/modules/Spinner';

// types
type Variant = `solid` | `outlined` | `text`;

type Size = `small` | `medium` | `large`;

type Color = `primary` | `secondary` | `danger` | `default` | `white`;

type VariantColor = `${Variant}-${Color}`;

export type ButtonProps = {
  variant?: Variant;
  color?: Color;
  size?: Size;
  isLoading?: boolean;
  children: ReactNode;
  disabled?: boolean;

  // Button icons
  startIcon?: ReactNode;
  endIcon?: ReactNode;
} & Omit<TypographyProps<'button'>, 'variant'>;

// button variants
const SIZE_MAPS: Record<Size, string> = {
  small: `py-1 px-2.5 text-sm min-w-[75px]`,
  medium: `py-1 md:py-2 px-3 text-md min-w-[100px]`,
  large: `py-2 px-3 text-lg min-w-[150px]`,
};

const COLOR_MAPS: Record<VariantColor, string> = {
  //* SOLID */
  'solid-primary': `bg-green-600 hover:bg-green-700 active:bg-green-800 focus:ring-2 focus:ring-green-300 text-white`,
  'solid-secondary': `bg-purple-600 hover:bg-purple-700 active:bg-purple-800 focus:ring-2 focus:ring-purple-300 text-white`,
  'solid-default': `bg-gray-500 hover:bg-gray-600 active:bg-gray-700 focus:ring-2 focus:ring-gray-300 text-white`,
  'solid-danger': `bg-red-500 hover:bg-red-600 active:bg-red-700 focus:ring-2 focus:ring-red-300 text-white`,
  'solid-white': `bg-white hover:bg-gray-50 active:bg-gray-100 focus:ring-2 focus:ring-gray-50 text-gray-700`,
  //* OUTLINED */
  'outlined-default': `border-2 border-gray-200 hover:bg-gray-500 hover:bg-opacity-10 active:bg-opacity-0 text-gray-400 focus:ring-2 focus:ring-gray-100`,
  'outlined-primary': `border-2 border-green-600 hover:bg-green-700 hover:bg-opacity-10 active:bg-opacity-0 text-green-600 focus:ring-2 focus:ring-green-200`,
  'outlined-secondary': `border-2 border-purple-600 hover:bg-purple-700 hover:bg-opacity-10 active:bg-opacity-0 text-purple-600 focus:ring-2 focus:ring-purple-200`,
  'outlined-danger': `border-2 border-red-200 hover:bg-red-500 hover:bg-opacity-10 active:bg-opacity-0 text-red-200 focus:ring-2 focus:ring-red-100`,
  'outlined-white': `border-2 border-white hover:bg-gray-50 hover:bg-opacity-10 active:bg-opacity-0 text-gray-700 focus:ring-2 focus:ring-gray-50`,
  //* TEXT */
  'text-danger': `text-red-600 !p-0 min-w-[auto]`,
  'text-default': `text-gray-500 !p-0 min-w-[auto]`,
  'text-primary': `text-green-600 !p-0 min-w-[auto]`,
  'text-secondary': `text-purple-600 !p-0 min-w-[auto]`,
  'text-white': `text-gray-700 !p-0 min-w-[auto]`,
};

/**
 * @component Button
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>((props, ref) => {
  const {
    variant = `solid`,
    size = `medium`,
    color = `default`,
    startIcon,
    endIcon,
    isLoading,
    children,
    disabled,
    onClick,
    className,
    ...rest
  } = props;

  const classes = clsx(
    `transition select-none font-medium focus:outline-none flex flex-row items-center justify-center rounded-lg`,
    { 'opacity-50 select-none cursor-not-allowed': isLoading },
    {
      'bg-gray-300 text-gray-400 select-none cursor-not-allowed': disabled,
    },
    { [COLOR_MAPS[`${variant}-${color}` as VariantColor]]: !disabled },
    SIZE_MAPS[size],
    className,
  );

  return (
    <Typography
      ref={ref}
      variant="h6"
      as="button"
      onClick={!disabled ? onClick : () => {}}
      className={classes}
      {...rest}
    >
      {startIcon && !isLoading && <div className="mr-2">{startIcon}</div>}

      {isLoading && (
        <div className="mr-2">
          <Spinner size="medium" />
        </div>
      )}
      {children}

      {endIcon && <div className="ml-2 ">{endIcon}</div>}
    </Typography>
  );
});

export default Button;
